import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Calendar, TrendingUp } from 'lucide-react'; 
import UnitSelector from '../components/UnitSelector'; 
import RilisProduksiChart from '../components/RilisProduksiChart'; 
import RilisPackingPlantChart from '../components/RilisPackingPlantChart'; 

const API_URL = process.env.REACT_APP_API_URL;

const today = new Date();
const monthNames = [
    "Januari", "Februari", "Maret", "April", "Mei", "Juni", 
    "Juli", "Agustus", "September", "Oktober", "November", "Desember"
];

const MasterDashboard = () => {
    // State Filter Global (Hanya Tahun)
    const [selectedYear, setSelectedYear] = useState(today.getFullYear());
    
    // State Rilis Data
    const [rilisDataStates, setRilisDataStates] = useState({
        pabrik:      [],
        bks:         [],
        packing:     [],
    });

    // STATE MASTER DATA BARU
    const [allUnits, setAllUnits] = useState([]);
    
    // STATE BARU untuk total spesifik Unit Packing Plant
    // Tidak lagi menggunakan state ini, akan dihitung di useMemo
    const [selectedPackingUnit, setSelectedPackingUnit] = useState(null); 
    
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();
    
    const availableYears = [2025, 2024, 2023]; 

    // --- FETCH MASTER UNIT (GLOBAL) ---
    useEffect(() => {
        const fetchMasterUnits = async () => {
            try {
                const res = await axios.get(`${API_URL}/api/units`); 
                setAllUnits(res.data);
                setIsLoading(false);
            } catch (err) {
                console.error("Gagal fetch unit master:", err);
                setIsLoading(false);
            }
        };
        fetchMasterUnits();
    }, []);
    
    // --- FETCH DATA RILIS (Agregasi Tahun Bulanan) ---
    const fetchRilisData = async (moduleKey, apiPath) => {
        try {
            const endpoint = `${API_URL}/api/${apiPath}/rilis/${selectedYear}`;
            const res = await axios.get(endpoint);
            
            setRilisDataStates(prev => ({
                ...prev,
                [moduleKey]: res.data || []
            }));
        } catch (err) {
            console.error(`Error fetching Rilis data for ${moduleKey}:`, err);
        }
    };
    
    // --- FUNGSI AGREGASI HELPER ---
    const calculateTotalRilis = (data) => {
        if (!data || data.length === 0) return 0;
        
        let total = 0;
        data.forEach(monthData => {
            Object.keys(monthData).forEach(key => {
                if (key !== 'month' && key !== 'monthLabel' && key !== 'RKAP' && key !== 'TOTAL_PRODUKSI') {
                    total += parseFloat(monthData[key]) || 0;
                }
            });
        });
        return total;
    };
    
    // --- LOGIKA BARU: HITUNG TOTAL YTD UNIT TERPILIH DARI DATA RILIS YANG SUDAH ADA ---
    const packingUnitTotalYTD = useMemo(() => {
        if (!selectedPackingUnit || rilisDataStates.packing.length === 0) return 0;
        
        const unitName = allUnits.find(u => u.id_unit.toString() === selectedPackingUnit)?.nama_unit;
        if (!unitName) return 0;

        let total = 0;
        
        // Iterasi semua data bulanan (dari rilisDataStates.packing)
        // dan jumlahkan nilai berdasarkan nama unit (yang menjadi key di data rilis)
        rilisDataStates.packing.forEach(monthData => {
             const unitValue = parseFloat(monthData[unitName]) || 0;
             total += unitValue;
        });

        return total;
    }, [selectedPackingUnit, rilisDataStates.packing, allUnits]);


    // EFFECT 1: Mengambil data Rilis (Global)
    useEffect(() => {
        fetchRilisData('pabrik', 'produksi/pabrik');
        fetchRilisData('bks', 'produksi/bks');
        fetchRilisData('packing', 'packing-plant');
    }, [selectedYear]); 
    
    // EFFECT 2: fetchSpecificTotal DIHAPUS karena sudah digantikan oleh useMemo di atas


    // --- RENDER HELPERS ---
    const formatValue = (value) => {
        if (value === 'N/A') return value;
        const numericValue = parseFloat(value);
        return isNaN(numericValue) ? 0 : numericValue.toLocaleString(undefined, { maximumFractionDigits: 0 });
    };

    const yearDisplay = `${selectedYear}`;
    
    // --- RENDERING UTAMA ---
    return (
        <div className="min-h-screen bg-gray-50 p-4 sm:p-6 md:p-8">
            <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-800 mb-2">
                Rilis Produksi
            </h1>
            <p className="text-gray-500 mb-8">Perbandingan target dan realisasi.</p>

            {/* Global Filters & Time Display (Hanya Tahun) */}
            <div className="bg-white p-4 rounded-xl shadow-lg border border-gray-100 mb-10">
                <div className="flex flex-wrap gap-4 items-center">
                    
                    {/* Display Periode Aktif */}
                    <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                        <Calendar size={18} className="text-blue-500" />
                        <span className="mr-2">Tahun Aktif:</span>
                    </div>
                    
                    {/* Selector Tahun */}
                    <select value={selectedYear} onChange={(e) => setSelectedYear(parseInt(e.target.value))} className="p-2 border border-gray-300 rounded-lg text-sm bg-gray-50 transition-shadow focus:ring-2 focus:ring-purple-200">
                        {availableYears.map(year => (<option key={year} value={year}>{year}</option>))}
                    </select>
                </div>
            </div>
            
            {isLoading ? (
                <p className="text-center p-10 text-blue-600 animate-pulse">Memuat data rilis bulanan...</p>
            ) : (
                <div className="space-y-6"> 
                    
                    {/* 1. Rilis Produksi Pabrik */}
                    <div className="w-full bg-white p-6 rounded-xl shadow-lg border border-gray-100">
                         {/* Total Produksi Pabrik */}
                         <div className="flex items-center gap-3 mb-4 p-3 rounded-lg bg-red-50">
                             <TrendingUp size={24} className="text-red-600" />
                             <div>
                                 <p className="text-sm font-medium text-gray-600">Total Produksi Pabrik</p>
                                 <h4 className="text-2xl font-extrabold text-red-700">
                                     {formatValue(calculateTotalRilis(rilisDataStates.pabrik))} <span className="text-lg font-semibold">TON</span>
                                 </h4>
                             </div>
                         </div>
                         <RilisProduksiChart 
                             rilisData={rilisDataStates.pabrik} 
                             selectedYear={selectedYear} 
                             groupName="Pabrik" 
                         />
                    </div>

                    {/* 2. Rilis Produksi BKS (Pelabuhan) */}
                    <div className="w-full bg-white p-6 rounded-xl shadow-lg border border-gray-100">
                         {/* Total Produksi BKS */}
                         <div className="flex items-center gap-3 mb-4 p-3 rounded-lg bg-green-50">
                             <TrendingUp size={24} className="text-green-600" />
                             <div>
                                 <p className="text-sm font-medium text-gray-600">Total Produksi Pelabuhan</p>
                                 <h4 className="text-2xl font-extrabold text-green-700">
                                     {formatValue(calculateTotalRilis(rilisDataStates.bks))} <span className="text-lg font-semibold">TON</span>
                                 </h4>
                             </div>
                         </div>
                         <RilisProduksiChart 
                             rilisData={rilisDataStates.bks} 
                             selectedYear={selectedYear} 
                             groupName="BKS" 
                         />
                    </div>
                    
                    {/* 3. Rilis Packing Plant (DENGAN FILTER PER UNIT) */}
                    <div className="w-full bg-white p-6 rounded-xl shadow-lg border border-gray-100">
                         {/* Total Produksi Packing Plant */}
                         <div className="flex flex-col mb-4 p-3 rounded-lg bg-indigo-50">
                            <div className="flex items-center gap-3 justify-between"> {/* Flex untuk merapikan baris pertama */}
                                {/* KIRI: Total Agregat Grup */}
                                <div className="flex items-center gap-3">
                                    <TrendingUp size={24} className="text-indigo-600" />
                                    <div>
                                        <p className="text-sm font-medium text-gray-600">Total Produksi Packing Plant</p>
                                        <h4 className="text-2xl font-extrabold text-indigo-700">
                                            {formatValue(calculateTotalRilis(rilisDataStates.packing))} <span className="text-lg font-semibold">TON</span>
                                        </h4>
                                    </div>
                                </div>

                                {/* KANAN: Filter Unit Total */}
                                <div className="flex flex-col items-end">
                                    <p className="text-xs font-semibold text-gray-700 mb-1 whitespace-nowrap">Filter Unit Total:</p>
                                    <UnitSelector 
                                        onSelect={setSelectedPackingUnit} 
                                        selectedUnit={selectedPackingUnit} 
                                        allowedGroupName="Packing Plant" 
                                        allUnits={allUnits} 
                                    />
                                </div>
                            </div>

                            {/* HASIL FILTER UNIT TERPILIH */}
                            <div className="mt-4 pt-3 border-t border-indigo-200">
                                <div className="flex justify-between items-center bg-white p-3 rounded-md shadow-inner">
                                    <p className="text-sm font-medium text-gray-700">Total YTD Unit Terpilih ({yearDisplay}):</p>
                                    <p className="text-xl font-bold text-blue-600">{formatValue(packingUnitTotalYTD)} TON</p>
                                </div>
                            </div>
                         </div>

                         <RilisPackingPlantChart 
                             rilisData={rilisDataStates.packing} 
                             selectedYear={selectedYear} 
                             groupName="Packing Plant" 
                         />
                    </div>
                </div>
            )}
        </div>
    );
};


export default MasterDashboard;